import {
  isActivelyWorkedStatus,
  isOnRouteWorkItemStatus,
  isOnSiteWorkItemStatus,
  isSuccessfulCompletionStatus,
  normalizeWorkflowStatusKey,
} from './operationalStatusConfig';

export interface WorkItemProgressContext {
  id?: string | null;
  item_type?: string | null;
  status?: string | null;
  progress_percentage?: number | null;
  custom_fields?: Record<string, any> | string | null;
  children?: WorkItemProgressContext[] | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  assigned_users?: number[] | null;
}

export interface ProgressRollupCounts {
  total: number;
  considered: number;
  completed: number;
  active: number;
  pending: number;
  cancelled: number;
  missed: number;
}

export interface DerivedWorkItemProgress {
  progressPercentage: number;
  label: string;
  description: string;
  source: 'manual' | 'status' | 'rollup' | 'mixed';
  readOnly: boolean;
  counts?: ProgressRollupCounts;
}

const CLOSED_STATUSES = new Set(['finished', 'archived']);
const CANCELLED_STATUSES = new Set(['cancelled', 'canceled', 'void', 'skipped', 'apt_cancelled']);
const MISSED_STATUSES = new Set(['missed', 'no_show', 'failed']);
const RESOLVED_STATUSES = new Set(['resolved', 'apt_resolved']);
const FOLLOW_UP_STATUSES = new Set(['unresolved', 'partial', 'rescheduled', 'apt_partial', 'partially_complete']);
const TRAVEL_STATUSES = new Set(['traveling']);
const NOT_STARTED_STATUSES = new Set(['backlog', 'scheduled', 'assigned', 'confirmed', 'pending', 'open', 'new', 'dispatched', 'todo', 'planning']);

const APPOINTMENT_PROGRESS = {
  notStarted: 0,
  travel: 25,
  arrived: 50,
  active: 75,
  followUp: 90,
  resolved: 99,
  closed: 100,
} as const;

const clampPercentage = (value: unknown): number => {
  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return Math.max(0, Math.min(100, Math.round(numericValue)));
};

export const isOperationalHierarchyItemType = (itemType?: string | null): boolean => {
  return itemType === 'appointment' || itemType === 'job' || itemType === 'project';
};

export const normalizeProgressCustomFields = (value: WorkItemProgressContext['custom_fields']): Record<string, any> => {
  if (!value) {
    return {};
  }

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  return value && typeof value === 'object' ? value : {};
};

const normalizeStatus = (value?: string | null): string => normalizeWorkflowStatusKey(value);

const humanizeStatus = (status?: string | null): string =>
  normalizeStatus(status).replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) || 'Not started';

const isClosedLikeStatus = (status: string) => CLOSED_STATUSES.has(status) || (isSuccessfulCompletionStatus(status) && !RESOLVED_STATUSES.has(status));
const isCancelledLikeStatus = (status: string) => CANCELLED_STATUSES.has(status);
const isMissedLikeStatus = (status: string) => MISSED_STATUSES.has(status);
const isResolvedLikeStatus = (status: string) => RESOLVED_STATUSES.has(status);
const isFollowUpStatus = (status: string) => FOLLOW_UP_STATUSES.has(status);
const isActiveWorkStatus = (status: string) => isActivelyWorkedStatus(status);
const isArrivedStatus = (status: string) => isOnSiteWorkItemStatus(status);
const isTravelStatus = (status: string) => isOnRouteWorkItemStatus(status) || TRAVEL_STATUSES.has(status);
const isNotStartedStatus = (status: string) => NOT_STARTED_STATUSES.has(status);
const isAdminReopenedStatus = (status: string) => status === 'admin_reopened';

const deriveManualProgress = (context: WorkItemProgressContext): DerivedWorkItemProgress => {
  const status = normalizeStatus(context.status);
  const recordedProgress = clampPercentage(context.progress_percentage);

  if (isAdminReopenedStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.closed,
      label: 'Admin re-opened',
      description: 'This work item was previously closed and reopened for admin follow-up. Operational progress remains complete.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isClosedLikeStatus(status)) {
    return {
      progressPercentage: 100,
      label: 'Closed',
      description: 'This work item is closed.',
      source: 'status',
      readOnly: false,
    };
  }

  if (isResolvedLikeStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.resolved,
      label: 'Resolved',
      description: 'Field work is complete and awaiting final close-out.',
      source: 'status',
      readOnly: false,
    };
  }

  return {
    progressPercentage: recordedProgress,
    label: recordedProgress > 0 ? 'Manual' : 'Not started',
    description: recordedProgress > 0
      ? 'Progress can be adjusted manually for this work item type.'
      : 'Use the quick controls to set progress manually.',
    source: 'manual',
    readOnly: false,
  };
};

const deriveAppointmentProgress = (context: WorkItemProgressContext): DerivedWorkItemProgress => {
  const status = normalizeStatus(context.status);
  const customFields = normalizeProgressCustomFields(context.custom_fields);
  const recordedProgress = clampPercentage(context.progress_percentage);
  const hasTravelStarted = Boolean(customFields.travel_started_at || customFields.completed_travel_entry || customFields.travel_time_minutes);
  const hasArrived = Boolean(customFields.arrived_at);
  const hasCheckedIn = Boolean(customFields.check_in_timestamp);
  const hasCheckedOut = Boolean(customFields.check_out_timestamp);

  if (isAdminReopenedStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.closed,
      label: 'Admin re-opened',
      description: 'This appointment was reopened for admin follow-up after close-out. Progress stays at 100%.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isClosedLikeStatus(status) || isCancelledLikeStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.closed,
      label: isCancelledLikeStatus(status) ? 'Cancelled' : 'Closed',
      description: isCancelledLikeStatus(status)
        ? 'This appointment has reached a terminal outcome.'
        : 'This appointment is fully closed.',
      source: 'status',
      readOnly: true,
    };
  }

  // Missed / no-show / failed visits are terminal scheduling failures: they
  // contribute 0% progress but remain in the roll-up denominator so parent
  // jobs reflect under-delivery instead of silently shrinking the scope.
  if (isMissedLikeStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.notStarted,
      label: humanizeStatus(status),
      description: 'The scheduled slot passed without this appointment being completed.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isResolvedLikeStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.resolved,
      label: 'Resolved',
      description: 'Field work is complete; admin close-out is the final step.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isFollowUpStatus(status) || hasCheckedOut) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.followUp,
      label: 'Follow-up',
      description: 'The visit is finished, but follow-up or admin action is still required.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isActiveWorkStatus(status) || hasCheckedIn) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.active,
      label: 'On site',
      description: 'Progress is driven by SmartTask once the technician clocks in on site.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isArrivedStatus(status) || hasArrived) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.arrived,
      label: 'Arrived',
      description: 'The technician has arrived on site and is ready to begin work.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isTravelStatus(status) || hasTravelStarted) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.travel,
      label: 'En route',
      description: 'Travel has started, so progress is now tracked from the field workflow.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isNotStartedStatus(status) || !status) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.notStarted,
      label: 'Not started',
      description: 'This appointment has not started yet.',
      source: 'status',
      readOnly: true,
    };
  }

  return {
    progressPercentage: recordedProgress,
    label: recordedProgress > 0 ? 'Manual' : 'Not started',
    description: recordedProgress > 0
      ? 'Using the stored progress value because no SmartTask lifecycle signal was found.'
      : 'This appointment has no operational progress signal yet.',
    source: recordedProgress > 0 ? 'manual' : 'status',
    readOnly: true,
  };
};

const summarizeChildProgress = (children: WorkItemProgressContext[]): {
  summaries: DerivedWorkItemProgress[];
  counts: ProgressRollupCounts;
  averageProgress: number;
} => {
  const summaries = children.map((child) => deriveWorkItemProgress(child));
  let consideredProgressTotal = 0;

  const counts = children.reduce<ProgressRollupCounts>((acc, child, index) => {
    const summary = summaries[index];
    const childStatus = normalizeStatus(child.status);

    acc.total += 1;

    if (isCancelledLikeStatus(childStatus)) {
      acc.cancelled += 1;
      return acc;
    }

    // Missed appointments stay in the denominator (considered) with 0%
    // progress — a missed visit is a scheduling failure, not removed scope.
    if (isMissedLikeStatus(childStatus)) {
      acc.missed += 1;
      acc.considered += 1;
      return acc;
    }

    acc.considered += 1;
    consideredProgressTotal += summary.progressPercentage;

    if (summary.progressPercentage >= 99) {
      acc.completed += 1;
    } else if (summary.progressPercentage > 0) {
      acc.active += 1;
    } else {
      acc.pending += 1;
    }

    return acc;
  }, { total: 0, considered: 0, completed: 0, active: 0, pending: 0, cancelled: 0, missed: 0 });

  const averageProgress = counts.considered > 0
    ? Math.round(consideredProgressTotal / counts.considered)
    : 0;

  return { summaries, counts, averageProgress };
};

const buildRollupDescription = (
  childLabel: string,
  counts: ProgressRollupCounts,
  fallbackDescription: string,
): string => {
  if (counts.total === 0) {
    return fallbackDescription;
  }

  const parts = [`Roll-up from ${counts.total} ${childLabel}${counts.total === 1 ? '' : 's'}`];
  if (counts.completed > 0) {
    parts.push(`${counts.completed} complete`);
  }
  if (counts.active > 0) {
    parts.push(`${counts.active} active`);
  }
  if (counts.pending > 0) {
    parts.push(`${counts.pending} pending`);
  }
  if (counts.missed > 0) {
    parts.push(`${counts.missed} missed`);
  }
  if (counts.cancelled > 0) {
    parts.push(`${counts.cancelled} cancelled`);
  }

  return parts.join(' · ');
};

const deriveJobProgress = (context: WorkItemProgressContext): DerivedWorkItemProgress => {
  const status = normalizeStatus(context.status);
  const children = Array.isArray(context.children) ? context.children : [];

  if (isAdminReopenedStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.closed,
      label: 'Admin re-opened',
      description: 'This job was reopened for admin follow-up after close-out. Operational progress remains complete.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isClosedLikeStatus(status) || isCancelledLikeStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.closed,
      label: isCancelledLikeStatus(status) ? 'Cancelled' : 'Closed',
      description: 'This job is in a terminal state.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isResolvedLikeStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.resolved,
      label: 'Resolved',
      description: 'Field delivery is complete and awaiting final admin close-out.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isFollowUpStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.followUp,
      label: 'Follow-up',
      description: 'This job has been completed operationally, but follow-up is still required.',
      source: 'status',
      readOnly: true,
    };
  }

  if (children.length === 0) {
    const baseAppointmentSummary = deriveAppointmentProgress(context);
    return {
      ...baseAppointmentSummary,
      description: 'No child appointments are linked yet, so progress follows the job status.',
    };
  }

  const { counts, averageProgress } = summarizeChildProgress(children);
  const hasRollupSignals = counts.considered > 0 || counts.cancelled > 0;

  if (isNotStartedStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.notStarted,
      label: humanizeStatus(status),
      description: hasRollupSignals
        ? `${buildRollupDescription('appointment', counts, 'Linked appointment activity is available.')} The job itself is still ${humanizeStatus(status).toLowerCase()}, so the headline progress stays at 0% until work starts.`
        : 'This job is scheduled and waiting for operational work to begin.',
      source: hasRollupSignals ? 'mixed' : 'status',
      readOnly: true,
      counts,
    };
  }

  let progressPercentage = averageProgress;
  let source: DerivedWorkItemProgress['source'] = 'rollup';
  const allChildrenComplete = counts.considered > 0
    && counts.completed === counts.considered
    && counts.active === 0
    && counts.pending === 0;

  if (allChildrenComplete) {
    progressPercentage = APPOINTMENT_PROGRESS.resolved;
    source = 'mixed';
  }

  if (isTravelStatus(status) && progressPercentage < APPOINTMENT_PROGRESS.travel) {
    progressPercentage = APPOINTMENT_PROGRESS.travel;
    source = 'mixed';
  }

  if ((isArrivedStatus(status) || isActiveWorkStatus(status)) && progressPercentage < APPOINTMENT_PROGRESS.arrived) {
    progressPercentage = APPOINTMENT_PROGRESS.arrived;
    source = 'mixed';
  }

  return {
    progressPercentage: clampPercentage(progressPercentage),
    label: 'Appointment roll-up',
    description: allChildrenComplete
      ? 'All linked appointments are operationally complete; final job close-out is still pending.'
      : buildRollupDescription('appointment', counts, 'Progress follows linked appointment activity.'),
    source,
    readOnly: true,
    counts,
  };
};

const deriveProjectProgress = (context: WorkItemProgressContext): DerivedWorkItemProgress => {
  const status = normalizeStatus(context.status);
  const children = Array.isArray(context.children) ? context.children : [];

  if (isAdminReopenedStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.closed,
      label: 'Admin re-opened',
      description: 'This project was reopened for admin follow-up after close-out. Operational progress remains complete.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isClosedLikeStatus(status) || isCancelledLikeStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.closed,
      label: isCancelledLikeStatus(status) ? 'Cancelled' : 'Closed',
      description: 'This project is in a terminal state.',
      source: 'status',
      readOnly: true,
    };
  }

  if (isResolvedLikeStatus(status)) {
    return {
      progressPercentage: APPOINTMENT_PROGRESS.resolved,
      label: 'Resolved',
      description: 'All operational delivery is complete; final close-out is still pending.',
      source: 'status',
      readOnly: true,
    };
  }

  if (children.length === 0) {
    return {
      progressPercentage: clampPercentage(context.progress_percentage),
      label: 'Manual',
      description: 'No child jobs are linked yet, so progress falls back to the stored project value.',
      source: 'manual',
      readOnly: true,
    };
  }

  const { counts, averageProgress } = summarizeChildProgress(children);
  let progressPercentage = averageProgress;
  let source: DerivedWorkItemProgress['source'] = 'rollup';
  const allChildrenComplete = counts.considered > 0
    && counts.completed === counts.considered
    && counts.active === 0
    && counts.pending === 0;

  if (allChildrenComplete) {
    progressPercentage = APPOINTMENT_PROGRESS.resolved;
    source = 'mixed';
  }

  if ((isActiveWorkStatus(status) || isTravelStatus(status) || status === 'active') && progressPercentage < APPOINTMENT_PROGRESS.travel) {
    progressPercentage = APPOINTMENT_PROGRESS.travel;
    source = 'mixed';
  }

  return {
    progressPercentage: clampPercentage(progressPercentage),
    label: 'Job roll-up',
    description: allChildrenComplete
      ? 'All linked jobs are operationally complete; final project close-out is still pending.'
      : buildRollupDescription('job', counts, 'Progress follows linked job delivery.'),
    source,
    readOnly: true,
    counts,
  };
};

export function deriveWorkItemProgress(context: WorkItemProgressContext): DerivedWorkItemProgress {
  switch (context.item_type) {
    case 'appointment':
      return deriveAppointmentProgress(context);
    case 'job':
      return deriveJobProgress(context);
    case 'project':
      return deriveProjectProgress(context);
    default:
      return deriveManualProgress(context);
  }
}